import type { ComponentType, MouseEvent } from 'react';
import { Link } from 'wouter';
import { ChevronRight, type LucideProps } from 'lucide-react';
import { CmsSchema, useCmsFields } from '@/cms/content';

/**
 * Wspólny layout dla rodziny sekcji SEO pod listingami kategorii
 * (Kosze/Torebka/Kwiaty/Flowerbox/Bukiety) — wyodrębniony z pięciu niemal
 * identycznych komponentów. Odpowiada WYŁĄCZNIE za wspólny układ; wszystko,
 * co różni poszczególne kategorie, jest daną w configu (patrz
 * `src/config/categorySeoShowcase.ts`), nie osobnym komponentem ani
 * warunkiem w JSX. Celowo NIE łączony z `CollectionSeoSection.tsx` — to
 * inny, odrębny wzorzec (karty z numerowanym akapitem, bez zdjęć/pigułek).
 */

/* ── Tokens — identyczne w każdym z pięciu oryginalnych plików ─── */
const BG      = '#fff';
const GOLD    = '#e05590';
const DARK    = '#2A1F14';
const ROSE    = '#9B2335';
const MUTED   = 'rgba(42,31,20,0.52)';
const ICON_BG = '#FAE8E8';

export type CategorySeoIcon = ComponentType<LucideProps>;

/**
 * Tytuł karty/podtytuł sekcji — string (jedna linia, jak w Kosze/Torebka/
 * Kwiaty) albo krotka dwóch linii złączonych `<br />` (jak w Flowerbox/
 * Bukiety, np. `['Na', 'urodziny']`). Oba warianty istnieją dziś w kodzie
 * 1:1 — to nie jest nowy pomysł, tylko nazwany istniejący wzorzec.
 */
export type CategorySeoText = string | [string, string];

export interface CategorySeoCard {
  Icon: CategorySeoIcon;
  title: CategorySeoText;
  desc: string;
  image: string;
  imageAlt: string;
  linkLabel: string;
  href: string;
}

export interface CategorySeoPill {
  Icon: CategorySeoIcon;
  label: string;
  href: string;
}

export interface CategorySeoShowcaseConfig {
  sectionAriaLabel: string;
  eyebrow: string;
  heading: string;
  subtitle: CategorySeoText;
  /** `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))` — 220 wszędzie poza Flowerbox (200). */
  cardMinWidth: number;
  /** Jedyna dziś obserwowana różnica w stylu karty (blur cienia 12 vs 14px) — wartość, nie wariant. */
  cardBoxShadow: string;
  cards: CategorySeoCard[];
  pillsLabel: string;
  pills: CategorySeoPill[];
}

export function CategorySeoShowcase({ config }: { config: CategorySeoShowcaseConfig }) {
  const { sectionAriaLabel, eyebrow, heading, subtitle, cardMinWidth, cardBoxShadow, cards, pillsLabel, pills } = config;
  const lineFields = (key: string, label: string, value: CategorySeoText) => (typeof value === 'string' ? [value] : value).map((line, index) => ({
    key: `${key}.${index}`, label: `${label}${index ? ' — druga linia' : ''}`, section: 'Opis kategorii', kind: 'text' as const, defaultValue: line, required: true,
  }));
  const cms = useCmsFields([
    { key: 'category.showcase.eyebrow', label: 'Nadtytuł', section: 'Opis kategorii', kind: 'text', defaultValue: eyebrow },
    { key: 'category.showcase.heading', label: 'Nagłówek sekcji', section: 'Opis kategorii', kind: 'text', defaultValue: heading, required: true },
    ...lineFields('category.showcase.subtitle', 'Wprowadzenie', subtitle),
    ...cards.flatMap((card, index) => [
      ...lineFields(`category.showcase.cards.${index}.title`, `Karta ${index + 1}: tytuł`, card.title),
      { key: `category.showcase.cards.${index}.description`, label: `Karta ${index + 1}: opis`, section: 'Opis kategorii', kind: 'textarea' as const, defaultValue: card.desc, required: true },
      { key: `category.showcase.cards.${index}.image`, label: `Karta ${index + 1}: zdjęcie`, section: 'Opis kategorii', kind: 'image' as const, defaultValue: card.image, required: true },
      { key: `category.showcase.cards.${index}.alt`, label: `Karta ${index + 1}: opis zdjęcia`, section: 'Opis kategorii', kind: 'alt' as const, defaultValue: card.imageAlt },
      { key: `category.showcase.cards.${index}.linkLabel`, label: `Karta ${index + 1}: tekst linku`, section: 'Opis kategorii', kind: 'text' as const, defaultValue: card.linkLabel, required: true },
    ]),
  ]);
  const lines = (key: string, value: CategorySeoText) => typeof value === 'string'
    ? <span {...cms.text(`${key}.0`)}>{cms.value(`${key}.0`)}</span>
    : <><span {...cms.text(`${key}.0`)}>{cms.value(`${key}.0`)}</span><br /><span {...cms.text(`${key}.1`)}>{cms.value(`${key}.1`)}</span></>;

  return (
    <section aria-label={sectionAriaLabel}>
      <CmsSchema fields={cms.fields} />
      {/* ── Main content ──────────────────────────────────────── */}
      <div className="page-container" style={{ background: BG, paddingBlock: 'clamp(32px, 5vw, 52px)' }}>

        {/* Eyebrow */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 14, marginBottom: 'clamp(20px, 3vw, 28px)',
        }}>
          <span style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 9.5,
            letterSpacing: '0.38em', textTransform: 'uppercase', color: GOLD,
          }}>
            <span {...cms.text('category.showcase.eyebrow')}>{cms.value('category.showcase.eyebrow')}</span>
          </span>
        </div>

        {/* Heading */}
        <h2 style={{
          fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontSize: 'clamp(2rem, 4.5vw, 3.2rem)',
          fontWeight: 400, lineHeight: 1.1, color: DARK,
          textAlign: 'center', margin: '0 0 clamp(12px, 2vw, 18px)',
          letterSpacing: '-0.01em',
        }}>
          <span {...cms.text('category.showcase.heading')}>{cms.value('category.showcase.heading')}</span>
        </h2>

        {/* Subtitle */}
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 'clamp(13px, 1.5vw, 15px)',
          color: MUTED, textAlign: 'center', lineHeight: 1.7,
          margin: '0 auto clamp(32px, 5vw, 52px)', maxWidth: 560,
        }}>
          {lines('category.showcase.subtitle', subtitle)}
        </p>

        {/* Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${cardMinWidth}px, 1fr))`,
          gap: 'clamp(12px, 2vw, 20px)',
        }}>
          {cards.map(({ Icon, title, desc, image, imageAlt, linkLabel, href }, i) => (
            <article
              key={i}
              style={{
                background: '#ffffff',
                borderRadius: 12,
                border: '1px solid rgba(210,60,120,0.18)',
                boxShadow: cardBoxShadow,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Card top — icon + title + desc */}
              <div style={{ padding: 'clamp(16px, 2.5vw, 24px) clamp(16px, 2.5vw, 24px) clamp(12px, 2vw, 20px)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 'clamp(10px, 1.5vw, 14px)' }}>
                  <span style={{
                    flexShrink: 0,
                    width: 44, height: 44,
                    borderRadius: '50%',
                    background: ICON_BG,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={20} color={ROSE} strokeWidth={1.6} />
                  </span>
                  <h3 style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 'clamp(14px, 1.6vw, 16px)',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: DARK,
                    margin: 0,
                    paddingTop: 3,
                  }}>
                    {lines(`category.showcase.cards.${i}.title`, title)}
                  </h3>
                </div>
                <p style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 'clamp(12px, 1.3vw, 13.5px)',
                  lineHeight: 1.65,
                  color: MUTED,
                  margin: 0,
                }}>
                  <span {...cms.text(`category.showcase.cards.${i}.description`)}>{cms.value(`category.showcase.cards.${i}.description`)}</span>
                </p>
              </div>

              {/* Photo */}
              <div style={{ flex: 1, minHeight: 'clamp(180px, 20vw, 240px)', overflow: 'hidden' }}>
                <img
                  {...cms.image(`category.showcase.cards.${i}.image`)}
                  {...cms.alt(`category.showcase.cards.${i}.alt`)}
                  src={cms.value(`category.showcase.cards.${i}.image`)}
                  alt={cms.value(`category.showcase.cards.${i}.alt`)}
                  loading="lazy"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center top',
                    display: 'block',
                  }}
                />
              </div>

              {/* Link */}
              <div style={{ padding: 'clamp(12px, 1.8vw, 16px) clamp(16px, 2.5vw, 24px)' }}>
                <Link
                  href={href}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 'clamp(12px, 1.3vw, 13px)',
                    color: ROSE, textDecoration: 'none',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontWeight: 500,
                  }}
                >
                  <span {...cms.text(`category.showcase.cards.${i}.linkLabel`)}>{cms.value(`category.showcase.cards.${i}.linkLabel`)}</span>
                  <ChevronRight size={14} strokeWidth={2} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* ── Bottom pill bar ───────────────────────────────────── */}
      <div className="page-container" style={{
        background: BG,
        borderTop: '1px solid rgba(210,60,120,0.18)',
        paddingBlock: 'clamp(20px, 3.5vw, 32px)',
      }}>
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 9,
          letterSpacing: '0.38em', textTransform: 'uppercase',
          color: GOLD, textAlign: 'center',
          margin: '0 0 clamp(12px, 2vw, 16px)',
        }}>
          {pillsLabel}
        </p>

        <div style={{
          display: 'flex', flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 'clamp(8px, 1.2vw, 10px)',
        }}>
          {pills.map(({ Icon, label, href }) => (
            <Link
              key={label}
              href={href}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '8px 16px', borderRadius: 999,
                border: '1px solid rgba(210,60,120,0.28)',
                background: '#ffffff',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
                color: DARK, textDecoration: 'none', whiteSpace: 'nowrap',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(210,60,120,0.6)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 1px 6px rgba(42,31,20,0.08)';
              }}
              onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(210,60,120,0.28)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
              }}
            >
              <Icon size={14} color={ROSE} strokeWidth={1.8} />
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
